/**
 * The Library tab: what the firm has shown its agents. The `ListPage`
 * toolbar (search, filter pills and ordering; all kept in the URL so a
 * filtered view can be shared or reloaded) over a folder rail and one table;
 * batches still in flight between them; the legacy import's status when it
 * has something to say. Uploads appear as rows at once and go live on their
 * own; there is no review step. Files dropped anywhere on the tab open the
 * upload dialog.
 *
 * The rail opens All Documents (every row, the flat view firms without
 * folders keep), Unfiled, or one folder (`?folder=`): its subfolders list
 * first, then its own documents. Search spans every folder and shows each
 * result's path unless the In: chip narrows it to the open folder; the
 * filters apply inside any folder. Rows drag onto a folder in the rail or the
 * table, or tick them and use Move To… from the batch bar. Click a row for
 * the drawer, Space for the peek.
 */
import {
  Bot,
  FileUp,
  Folder,
  FolderInput,
  FolderPlus,
  GripVertical,
  Loader2,
  MoreHorizontal,
  Pencil,
  RotateCw,
  Search,
  Sparkles,
  Trash2,
  X
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type FC,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode
} from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { firmKnowledgeApi, type FirmScope } from "@/api/firm-knowledge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { FilterChip } from "@/components/ui/filter-chip";
import { useListPeek } from "@/components/ui/peek-panel";
import {
  ClientTable,
  type ClientTableColumn
} from "@/features/clientProfile/components/table/ClientTable";
import { useOnScreen } from "@/features/agents/onScreen";
import { ClientTableRow } from "@/features/clientProfile/components/table/ClientTableRow";
import {
  ClientTableCell,
  MutedCell,
  TitleCell
} from "@/features/clientProfile/components/table/cells";
import { formatMdY } from "@/features/clientProfile/lib/format";
import { cn } from "@/lib/utils";

import type { FirmKnowledgeScope } from "../hooks/useFirmScope";
import {
  collectDroppedFiles,
  type CollectedFile
} from "../../caseFiles/upload/collectDroppedFiles";
import {
  ALL_DOCUMENTS,
  UNFILED,
  agentLibraryPath,
  canAddSubfolder,
  childFolders,
  descendantIds,
  entriesInScope,
  entriesUnder,
  folderChain,
  folderCounts,
  folderPathLabel,
  folderTree,
  isFolderScope,
  isUnfiled
} from "../folders";
import type { FirmLibraryState } from "../hooks/useFirmLibrary";
import {
  activeFacets,
  applyFilter,
  batchProgressLabel,
  CATEGORY_LABEL,
  categoryOf,
  collectDraftTypes,
  collectTags,
  collectVisas,
  filterFromParams,
  findRequestedEntry,
  isFiltering,
  migrationLabel,
  readinessOf,
  sortFromParam,
  sortLibrary,
  sortParam,
  writeFilterParams,
  type LibraryFacetKey,
  type LibraryFilter,
  type LibrarySort
} from "../model";
import type { FirmLibraryEntry, FirmLibraryFolder } from "../types";
import {
  ClientDetailsBadge,
  ConversionBadge,
  HashTags,
  NotReviewedBadge,
  ReadinessBadge
} from "./EntryBadges";
import { EntryDrawer } from "./EntryDrawer";
import {
  FolderRail,
  LIBRARY_DRAG_MIME,
  canDropOn,
  isLibraryDrag,
  type LibraryDrag
} from "./FolderRail";
import { LibraryFileIcon } from "./LibraryFileIcon";
import { LibraryPeek } from "./LibraryPeek";
import { LibraryTableToolbar } from "./LibraryTableToolbar";
import { MoveToDialog, type MoveSubject } from "./MoveToDialog";
import { UploadDialog } from "./UploadDialog";

type Props = {
  library: FirmLibraryState;
  firm: FirmKnowledgeScope;
  /** The upload dialog; the page header's Add Documents opens it too. */
  uploadOpen: boolean;
  onUploadOpenChange: (open: boolean) => void;
  /** The Library / Skills / Intake Templates segment, first in the toolbar. */
  toolbarLeading?: ReactNode;
};

const COLUMN_WIDTHS = {
  select: "40px",
  document: "52%",
  kind: "13%",
  pages: "8%",
  status: "15%",
  updated: "12%"
} as const;

/** "Document · 5", or "Document · 2 of 5" while a search narrows the list. */
export const libraryCountLabel = (shown: number, total: number): string =>
  shown === total ? `${total}` : `${shown} of ${total}`;

const plural = (n: number, one: string) => `${n} ${n === 1 ? one : `${one}s`}`;

const ROW_ID = "library-row-";

const entryIdFromElement = (el: Element): string | null =>
  el.closest(`tr[id^="${ROW_ID}"]`)?.id.slice(ROW_ID.length) || null;

const entryRowElement = (id: string) =>
  document.getElementById(`${ROW_ID}${id}`);

const columnsFor = (
  shown: number,
  total: number,
  loading: boolean,
  select: ReactNode | null
): ClientTableColumn[] => [
  ...(select
    ? [
        {
          id: "select",
          header: select,
          className: "justify-center",
          style: { width: COLUMN_WIDTHS.select }
        }
      ]
    : []),
  {
    id: "document",
    header: (
      <div className="flex items-center gap-1.5">
        <span>Document</span>
        {loading ? null : (
          <span className="text-fg-4 tabular-nums" data-testid="library-count">
            · {libraryCountLabel(shown, total)}
          </span>
        )}
      </div>
    ),
    style: { width: COLUMN_WIDTHS.document }
  },
  { id: "kind", header: "Kind", style: { width: COLUMN_WIDTHS.kind } },
  { id: "pages", header: "Pages", style: { width: COLUMN_WIDTHS.pages } },
  { id: "status", header: "Status", style: { width: COLUMN_WIDTHS.status } },
  { id: "updated", header: "Updated", style: { width: COLUMN_WIDTHS.updated } }
];

/** Retry and Remove on a row whose file could not be read. */
const AttentionActions: FC<{
  entry: FirmLibraryEntry;
  scope: FirmScope;
  onRetried: () => void;
  onRemoved: (entryId: string) => void;
}> = ({ entry, scope, onRetried, onRemoved }) => {
  const [busy, setBusy] = useState<"retry" | "remove" | null>(null);
  const [confirming, setConfirming] = useState(false);
  const name = entry.name || entry.original_filename || "this document";

  const retry = async () => {
    setBusy("retry");
    try {
      await firmKnowledgeApi.rerenderEntry(entry.id, scope);
      toast.success(`Reading “${name}” again`);
      onRetried();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Could not retry. Try again."
      );
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    setBusy("remove");
    try {
      await firmKnowledgeApi.deleteEntry(entry.id, scope);
      toast.success(`Removed “${name}”`);
      onRemoved(entry.id);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Could not remove it."
      );
      setBusy(null);
    }
  };

  return (
    <div className="flex items-center gap-1" data-testid="attention-actions">
      <Button
        size="xs"
        variant="secondary"
        disabled={busy !== null}
        onClick={() => void retry()}
        aria-label={`Retry reading ${name}`}
      >
        {busy === "retry" ? (
          <Loader2 className="size-3 animate-spin" />
        ) : (
          <RotateCw className="size-3" />
        )}
        Retry
      </Button>
      <Button
        size="xs"
        variant={confirming ? "danger" : "ghost"}
        disabled={busy !== null}
        onClick={() => (confirming ? void remove() : setConfirming(true))}
        onBlur={() => setConfirming(false)}
        aria-label={confirming ? `Confirm removing ${name}` : `Remove ${name}`}
      >
        {busy === "remove" ? (
          <Loader2 className="size-3 animate-spin" />
        ) : (
          <Trash2 className="size-3" />
        )}
        {confirming ? "Remove?" : "Remove"}
      </Button>
    </div>
  );
};

const documentSubtitle = (entry: FirmLibraryEntry) => {
  if (entry.description) return entry.description;
  if (entry.suggested?.status === "ready" && entry.suggested.description) {
    return `Suggested: ${entry.suggested.description}`;
  }
  return null;
};

const errorText = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;

/** "New Folder", or "New Folder 2" when that name is taken among siblings. */
const freshFolderName = (siblings: FirmLibraryFolder[]) => {
  const taken = new Set(siblings.map((f) => f.name.toLocaleLowerCase()));
  let name = "New Folder";
  for (let n = 2; taken.has(name.toLocaleLowerCase()); n += 1)
    name = `New Folder ${n}`;
  return name;
};

export const LibraryTab: FC<Props> = ({
  library,
  firm,
  uploadOpen,
  onUploadOpenChange: setUploadOpen,
  toolbarLeading
}) => {
  const { scope, canEditLibrary: canEdit } = firm;
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedEntry = searchParams.get("entry");
  const requestedFolder = searchParams.get("folder")?.trim() || ALL_DOCUMENTS;
  const filter = useMemo(() => filterFromParams(searchParams), [searchParams]);
  const sort = useMemo(
    () => sortFromParam(searchParams.get("sort")),
    [searchParams]
  );
  // Pills added from "+ Filter" but not yet given a value; set ones come from the URL.
  const [pendingFacets, setPendingFacets] = useState<LibraryFacetKey[]>([]);
  const [droppedFiles, setDroppedFiles] = useState<CollectedFile[] | null>(
    null
  );
  const [pageDrag, setPageDrag] = useState(false);
  const dragDepth = useRef(0);
  const [openId, setOpenId] = useState<string | null>(null);

  // Folders.
  const tree = useMemo(() => folderTree(library.folders), [library.folders]);
  const openFolderId =
    isFolderScope(requestedFolder) && tree.byId.has(requestedFolder)
      ? requestedFolder
      : null;
  const view =
    openFolderId ?? (requestedFolder === UNFILED ? UNFILED : ALL_DOCUMENTS);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [narrowSearch, setNarrowSearch] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [drag, setDrag] = useState<LibraryDrag | null>(null);
  const [overFolderRow, setOverFolderRow] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [moveSubject, setMoveSubject] = useState<MoveSubject | null>(null);
  // The prompt keeps its copy after closing so the exit animation doesn't blank it.
  const [deletePrompt, setDeletePrompt] = useState<{
    id: string;
    name: string;
    documents: number;
    subfolders: number;
  } | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const setFilter = useCallback(
    (next: LibraryFilter) =>
      setSearchParams((current) => writeFilterParams(current, next), {
        replace: true
      }),
    [setSearchParams]
  );
  const setSort = useCallback(
    (next: LibrarySort) =>
      setSearchParams(
        (current) => {
          const params = new URLSearchParams(current);
          const value = sortParam(next);
          if (value) params.set("sort", value);
          else params.delete("sort");
          return params;
        },
        { replace: true }
      ),
    [setSearchParams]
  );
  const shownFacets = useMemo(() => {
    const set = activeFacets(filter);
    return [...set, ...pendingFacets.filter((k) => !set.includes(k))];
  }, [filter, pendingFacets]);

  const setEntryParam = useCallback(
    (entryId: string | null) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (entryId) next.set("entry", entryId);
          else next.delete("entry");
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const expandTo = useCallback(
    (folderId: string) =>
      setExpanded((current) => {
        const chain = folderChain(tree, folderId).map((f) => f.id);
        const ancestors = chain.slice(0, -1);
        if (ancestors.every((id) => current.has(id))) return current;
        return new Set([...current, ...ancestors]);
      }),
    [tree]
  );

  const openScope = useCallback(
    (next: string) => {
      setSelected([]);
      setNarrowSearch(false);
      if (isFolderScope(next)) expandTo(next);
      setSearchParams((current) => {
        const params = new URLSearchParams(current);
        if (next === ALL_DOCUMENTS) params.delete("folder");
        else params.set("folder", next);
        return params;
      });
    },
    [expandTo, setSearchParams]
  );

  // A `?folder=` that names no folder (deleted, or another firm's) falls back.
  useEffect(() => {
    if (library.loading || !isFolderScope(requestedFolder)) return;
    if (tree.byId.has(requestedFolder)) expandTo(requestedFolder);
    else openScope(ALL_DOCUMENTS);
  }, [library.loading, requestedFolder, tree, expandTo, openScope]);

  // `?entry=` (a retired Studio link, a shared URL, or a Cmd+K jump while
  // another drawer is open) opens the drawer once the list is in; a legacy
  // id resolves through `source_ref`.
  useEffect(() => {
    if (!requestedEntry || library.loading || requestedEntry === openId) return;
    const found = findRequestedEntry(library.entries, requestedEntry);
    if (found) {
      setOpenId(found.id);
      if (found.id !== requestedEntry) setEntryParam(found.id);
    } else if (library.migration?.status !== "running") {
      setEntryParam(null);
    }
  }, [
    requestedEntry,
    library.loading,
    library.entries,
    library.migration,
    openId,
    setEntryParam
  ]);

  const openEntry = useCallback(
    (entry: FirmLibraryEntry) => {
      setOpenId(entry.id);
      setEntryParam(entry.id);
    },
    [setEntryParam]
  );
  const closeDrawer = (open: boolean) => {
    if (open) return;
    setOpenId(null);
    setEntryParam(null);
  };

  const openEntryRow = useMemo(
    () => library.entries.find((e) => e.id === openId) ?? null,
    [library.entries, openId]
  );
  useOnScreen(
    openEntryRow
      ? { kind: "library", id: openEntryRow.id, label: openEntryRow.name }
      : null
  );

  const { visible } = library;
  const searching = filter.query.trim().length > 0;
  const base = useMemo(
    () =>
      searching
        ? narrowSearch && openFolderId
          ? entriesUnder(tree, visible, openFolderId)
          : visible
        : entriesInScope(tree, visible, view),
    [searching, narrowSearch, openFolderId, tree, visible, view]
  );
  const rows = useMemo(
    () => sortLibrary(applyFilter(base, filter), sort),
    [base, filter, sort]
  );
  const subfolders = useMemo(
    () => (!searching && openFolderId ? childFolders(tree, openFolderId) : []),
    [searching, openFolderId, tree]
  );
  const counts = useMemo(() => folderCounts(tree, visible), [tree, visible]);
  const unfiledCount = useMemo(
    () => visible.filter((e) => isUnfiled(tree, e)).length,
    [tree, visible]
  );
  const choices = useMemo(
    () => ({
      tags: collectTags(library.active),
      visas: collectVisas(visible),
      draftTypes: collectDraftTypes(visible)
    }),
    [library.active, visible]
  );
  const hasFolders = library.folders.length > 0;
  const showPath = hasFolders && (searching || view === ALL_DOCUMENTS);

  const rowIds = useMemo(() => rows.map((e) => e.id), [rows]);
  const allSelected =
    rowIds.length > 0 && rowIds.every((id) => selected.includes(id));
  const selectHeader = canEdit ? (
    <Checkbox
      checked={allSelected ? true : selected.length ? "indeterminate" : false}
      onCheckedChange={(value) => setSelected(value === true ? rowIds : [])}
      aria-label="Select All Documents"
      disabled={rowIds.length === 0}
    />
  ) : null;
  const columns = columnsFor(
    rows.length,
    base.length,
    library.loading,
    selectHeader
  );
  const peek = useListPeek({
    ids: rowIds,
    onOpen: (id) => {
      const entry = rows.find((e) => e.id === id);
      if (entry) openEntry(entry);
    },
    onFocusRow: (id) => {
      const el = entryRowElement(id);
      el?.scrollIntoView?.({ block: "nearest" });
      el?.focus({ preventScroll: true });
    },
    enabled: openEntryRow === null && !uploadOpen && moveSubject === null
  });
  const peekEntry = peek.peekId
    ? rows.find((e) => e.id === peek.peekId) ?? null
    : null;
  const focusedId = peek.peekId ?? peek.activeId;
  // Library rows are focusable and treat Space as "open"; here it peeks.
  const listHandlers = {
    onMouseOver: (event: MouseEvent<HTMLDivElement>) =>
      peek.setHoverId(
        event.target instanceof Element
          ? entryIdFromElement(event.target)
          : null
      ),
    onFocus: (event: FocusEvent<HTMLDivElement>) =>
      peek.setHoverId(entryIdFromElement(event.target)),
    onMouseLeave: () => peek.setHoverId(null),
    onKeyDownCapture: (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== " " || !(event.target instanceof Element)) return;
      if (event.target.closest("button, input, a")) return;
      const id = entryIdFromElement(event.target);
      if (!id) return;
      event.preventDefault();
      event.stopPropagation();
      peek.toggle(id);
    }
  };
  const migrationNote = migrationLabel(library.migration);
  const filtering = isFiltering(filter);
  const notes = migrationNote || library.batches.length > 0;

  // --- moves -------------------------------------------------------------------

  const moveEntriesTo = useCallback(
    async (ids: string[], target: string | null) => {
      const moving = library.entries.filter(
        (e) => ids.includes(e.id) && (e.folder_id ?? null) !== target
      );
      if (!moving.length) return;
      library.replaceEntries(moving.map((e) => ({ ...e, folder_id: target })));
      try {
        const moved = await firmKnowledgeApi.moveEntries(
          moving.map((e) => e.id),
          target,
          scope
        );
        library.replaceEntries(moved);
        setSelected([]);
        const where = folderPathLabel(tree, target);
        const label =
          moving.length === 1
            ? `Moved “${moving[0].name || "the document"}” to ${where}`
            : `Moved ${moving.length} documents to ${where}`;
        const undo = async () => {
          const byFolder = new Map<string | null, string[]>();
          for (const e of moving) {
            const key = e.folder_id ?? null;
            byFolder.set(key, [...(byFolder.get(key) ?? []), e.id]);
          }
          try {
            const restored = await Promise.all(
              [...byFolder].map(([folderId, entryIds]) =>
                firmKnowledgeApi.moveEntries(entryIds, folderId, scope)
              )
            );
            library.replaceEntries(restored.flat());
          } catch (error) {
            toast.error(errorText(error, "Could not undo the move."));
          }
        };
        toast.success(label, {
          action: { label: "Undo", onClick: () => void undo() }
        });
      } catch (error) {
        library.replaceEntries(moving);
        toast.error(errorText(error, "Could not move them. Try again."));
      }
    },
    [library, scope, tree]
  );

  const moveFolderTo = useCallback(
    async (folderId: string, target: string | null) => {
      const folder = tree.byId.get(folderId);
      if (!folder || folder.parent_id === target) return;
      const previous = folder.parent_id;
      try {
        library.applyFolder(
          await firmKnowledgeApi.updateFolder(
            folderId,
            { parent_id: target },
            scope
          )
        );
        if (target) setExpanded((current) => new Set([...current, target]));
        toast.success(
          `Moved “${folder.name}” to ${
            target ? folderPathLabel(tree, target) : "the top level"
          }`,
          {
            action: {
              label: "Undo",
              onClick: () =>
                void firmKnowledgeApi
                  .updateFolder(folderId, { parent_id: previous }, scope)
                  .then(library.applyFolder)
                  .catch((error: unknown) =>
                    toast.error(errorText(error, "Could not undo the move."))
                  )
            }
          }
        );
      } catch (error) {
        toast.error(errorText(error, "Could not move the folder."));
      }
    },
    [library, scope, tree]
  );

  const onDrop = useCallback(
    (dropped: LibraryDrag, target: string | null) => {
      setDrag(null);
      if (dropped.kind === "entries") void moveEntriesTo(dropped.ids, target);
      else void moveFolderTo(dropped.id, target);
    },
    [moveEntriesTo, moveFolderTo]
  );

  const startEntryDrag = (event: DragEvent, entryId: string) => {
    const ids = selected.includes(entryId) ? selected : [entryId];
    event.dataTransfer.setData(LIBRARY_DRAG_MIME, JSON.stringify(ids));
    event.dataTransfer.effectAllowed = "move";
    setDrag({ kind: "entries", ids });
  };

  // --- folder actions ----------------------------------------------------------

  const createFolder = useCallback(
    async (name: string, parentId: string | null) => {
      try {
        const folder = await firmKnowledgeApi.createFolder(
          { name, parent_id: parentId },
          scope
        );
        library.applyFolder(folder);
        if (parentId) setExpanded((current) => new Set([...current, parentId]));
        return folder;
      } catch (error) {
        toast.error(errorText(error, "Could not create the folder."));
        return null;
      }
    },
    [library, scope]
  );

  const newFolder = async (parentId: string | null) => {
    const made = await createFolder(
      freshFolderName(childFolders(tree, parentId)),
      parentId
    );
    if (made) setRenamingId(made.id);
  };

  const renameFolder = async (folderId: string, name: string) => {
    setRenamingId(null);
    try {
      library.applyFolder(
        await firmKnowledgeApi.updateFolder(folderId, { name }, scope)
      );
    } catch (error) {
      toast.error(errorText(error, "Could not rename the folder."));
    }
  };

  const askDeleteFolder = (folderId: string) => {
    setDeletePrompt({
      id: folderId,
      name: tree.byId.get(folderId)?.name ?? "",
      documents: counts.get(folderId) ?? 0,
      subfolders: descendantIds(tree, folderId).length - 1
    });
    setDeleteOpen(true);
  };

  const deleteFolder = async (folderId: string) => {
    setDeleteOpen(false);
    const name = tree.byId.get(folderId)?.name ?? "the folder";
    try {
      const result = await firmKnowledgeApi.deleteFolder(folderId, scope);
      library.removeFolders(result.deleted_folder_ids);
      if (openFolderId && result.deleted_folder_ids.includes(openFolderId))
        openScope(UNFILED);
      toast.success(
        `Deleted “${name}”. ${plural(
          result.moved_entry_ids.length,
          "document"
        )} moved to Unfiled.`
      );
    } catch (error) {
      toast.error(errorText(error, "Could not delete the folder."));
    }
  };

  const folderActions = {
    onNewFolder: (parentId: string | null) => void newFolder(parentId),
    onRename: (folderId: string) => setRenamingId(folderId),
    onMove: (folderId: string) => setMoveSubject({ kind: "folder", folderId }),
    onDelete: askDeleteFolder
  };

  const moveCurrent = useMemo(() => {
    if (!moveSubject) return null;
    if (moveSubject.kind === "folder")
      return tree.byId.get(moveSubject.folderId)?.parent_id ?? null;
    const homes = new Set(
      library.entries
        .filter((e) => moveSubject.ids.includes(e.id))
        .map((e) => (isUnfiled(tree, e) ? null : e.folder_id ?? null))
    );
    return homes.size === 1 ? [...homes][0] : undefined;
  }, [moveSubject, library.entries, tree]);

  const applyMove = async (target: string | null) => {
    const subject = moveSubject;
    setMoveSubject(null);
    if (!subject) return;
    if (subject.kind === "folder") await moveFolderTo(subject.folderId, target);
    else await moveEntriesTo(subject.ids, target);
  };

  // --- page file drop ----------------------------------------------------------

  const hasFiles = (event: DragEvent) =>
    Array.from(event.dataTransfer?.types ?? []).includes("Files");
  const pageDropProps = canEdit
    ? {
        onDragEnter: (event: DragEvent<HTMLDivElement>) => {
          if (!hasFiles(event) || uploadOpen) return;
          dragDepth.current += 1;
          setPageDrag(true);
        },
        onDragOver: (event: DragEvent<HTMLDivElement>) => {
          if (!hasFiles(event) || uploadOpen) return;
          event.preventDefault();
        },
        onDragLeave: () => {
          dragDepth.current = Math.max(0, dragDepth.current - 1);
          if (dragDepth.current === 0) setPageDrag(false);
        },
        onDrop: (event: DragEvent<HTMLDivElement>) => {
          if (!hasFiles(event) || uploadOpen) return;
          event.preventDefault();
          dragDepth.current = 0;
          setPageDrag(false);
          void collectDroppedFiles(event.dataTransfer).then((collected) => {
            setDroppedFiles(collected);
            setUploadOpen(true);
          });
        }
      }
    : {};

  // --- the band over the table ---------------------------------------------------

  const openFolder = openFolderId ? tree.byId.get(openFolderId) : undefined;
  const crumbs = folderChain(tree, openFolderId);
  const band = (
    <div
      className="flex h-9 shrink-0 items-center gap-1.5 border-b border-line-2 pr-3 pl-4"
      data-testid="library-band"
    >
      {searching ? (
        <>
          <Search className="size-3.5 text-fg-3" aria-hidden />
          <span className="truncate text-base font-medium text-fg">
            Results for “{filter.query.trim()}”
          </span>
          <span className="text-base text-fg-3 tabular-nums">
            · {rows.length}
          </span>
          <span className="truncate text-sm text-fg-3">
            {narrowSearch && openFolder
              ? `in ${openFolder.name} and its subfolders`
              : "in every folder"}
          </span>
        </>
      ) : openFolder ? (
        <>
          <nav
            aria-label="Folder Path"
            className="flex min-w-0 items-center gap-1.5"
          >
            {crumbs.map((folder, i) => {
              const last = i === crumbs.length - 1;
              return (
                <span
                  key={folder.id}
                  className="flex min-w-0 items-center gap-1.5"
                >
                  {i > 0 ? (
                    <span className="text-base text-fg-4">/</span>
                  ) : null}
                  {last ? (
                    <span
                      className="truncate text-base font-medium text-fg"
                      aria-current="page"
                    >
                      {folder.name}
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="truncate text-base text-fg-3 hover:text-fg"
                      onClick={() => openScope(folder.id)}
                    >
                      {folder.name}
                    </button>
                  )}
                </span>
              );
            })}
          </nav>
          <span className="shrink-0 text-base text-fg-3 tabular-nums">
            · {counts.get(openFolder.id) ?? 0}
          </span>
          <div className="flex-1" />
          <span
            className="hidden shrink-0 items-center gap-1 text-sm text-fg-3 md:flex"
            title="Agents at the firm read every document in this folder."
          >
            <Bot className="size-3.5" aria-hidden />
            Agents Read This Folder
          </span>
          {canEdit ? (
            <>
              <Button
                variant="ghost"
                size="xs"
                disabled={!canAddSubfolder(tree, openFolder.id)}
                onClick={() => void newFolder(openFolder.id)}
                data-testid="band-new-subfolder"
              >
                <FolderPlus />
                New Subfolder
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label="Folder Actions"
                  >
                    <MoreHorizontal />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuItem
                    onSelect={() => setRenamingId(openFolder.id)}
                  >
                    <Pencil /> Rename
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => folderActions.onMove(openFolder.id)}
                  >
                    <FolderInput /> Move To…
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    variant="destructive"
                    onSelect={() => askDeleteFolder(openFolder.id)}
                  >
                    <Trash2 /> Delete Folder…
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : null}
        </>
      ) : (
        <>
          <span className="text-base font-medium text-fg">
            {view === UNFILED ? "Unfiled" : "All Documents"}
          </span>
          <span className="text-base text-fg-3 tabular-nums">
            · {view === UNFILED ? unfiledCount : visible.length}
          </span>
          <span className="truncate text-sm text-fg-3">
            {view === UNFILED
              ? "documents not in a folder; agents still read them"
              : "every folder"}
          </span>
        </>
      )}
    </div>
  );

  const folderRowDrop = (folderId: string) =>
    canEdit
      ? {
          onDragOver: (e: DragEvent<HTMLTableRowElement>) => {
            if (!isLibraryDrag(e) || !canDropOn(tree, drag, folderId)) return;
            e.preventDefault();
            setOverFolderRow(folderId);
          },
          onDragLeave: () => setOverFolderRow(null),
          onDrop: (e: DragEvent<HTMLTableRowElement>) => {
            if (!isLibraryDrag(e) || !drag) return;
            e.preventDefault();
            setOverFolderRow(null);
            if (canDropOn(tree, drag, folderId)) onDrop(drag, folderId);
          }
        }
      : {};

  return (
    <div
      className="relative flex h-full min-h-0"
      data-testid="library-tab"
      {...pageDropProps}
    >
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {pageDrag ? (
          <div
            className="pointer-events-none absolute inset-2 z-20 flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-accent-seed bg-accent-soft text-sm font-medium text-accent-seed"
            data-testid="library-drop-overlay"
          >
            <FileUp className="size-6" />
            {openFolder
              ? `Drop to add to ${openFolder.name}`
              : "Drop to add to the library"}
          </div>
        ) : null}
        <LibraryTableToolbar
          leading={toolbarLeading}
          filter={filter}
          onFilterChange={setFilter}
          shownFacets={shownFacets}
          onShownFacetsChange={(keys) =>
            setPendingFacets(
              keys.filter((k) => !activeFacets(filter).includes(k))
            )
          }
          choices={choices}
          sort={sort}
          onSortChange={setSort}
          onRefresh={() => void library.refresh()}
          folderScope={
            hasFolders ? { tree, value: view, onChange: openScope } : undefined
          }
          scopeChip={
            searching && openFolder ? (
              <FilterChip
                active={narrowSearch}
                onClick={() => setNarrowSearch((v) => !v)}
                aria-label="Search Only This Folder"
                data-testid="search-scope"
              >
                <Folder />
                In:{" "}
                <span className="max-w-40 truncate text-fg">
                  {narrowSearch ? openFolder.name : "All Folders"}
                </span>
              </FilterChip>
            ) : null
          }
        />

        {notes ? (
          <div className="flex flex-col gap-2 border-b border-line-2 px-4 py-3">
            {migrationNote ? (
              <div
                className="flex items-center gap-2 rounded-md bg-fill-1 px-3 py-2 text-sm text-fg-3"
                data-testid="migration-note"
              >
                {library.migration?.status === "running" ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : null}
                {migrationNote}
              </div>
            ) : null}

            {library.batches.map((batch) => (
              <div
                key={batch.id}
                className="flex items-center gap-2 rounded-md bg-accent-soft px-3 py-2 text-sm text-fg"
                data-testid="batch-row-summary"
              >
                {batch.status === "processing" ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Sparkles className="size-3.5 text-accent-seed" />
                )}
                {batchProgressLabel(batch)}
              </div>
            ))}
          </div>
        ) : null}

        <div className="flex min-h-0 flex-1">
          <FolderRail
            className="hidden min-[1100px]:flex"
            tree={tree}
            counts={counts}
            total={visible.length}
            unfiled={unfiledCount}
            selected={view}
            onSelect={openScope}
            expanded={expanded}
            onToggle={(folderId) =>
              setExpanded((current) => {
                const next = new Set(current);
                if (next.has(folderId)) next.delete(folderId);
                else next.add(folderId);
                return next;
              })
            }
            canEdit={canEdit}
            renamingId={renamingId}
            onRenameCommit={(folderId, name) =>
              void renameFolder(folderId, name)
            }
            onRenameCancel={() => setRenamingId(null)}
            drag={drag}
            onDragFolder={setDrag}
            onDrop={onDrop}
            {...folderActions}
          />

          <div
            className="relative flex min-h-0 min-w-0 flex-1 flex-col"
            {...listHandlers}
          >
            {band}
            <ClientTable
              ariaLabel="Firm library"
              className="min-h-0 flex-1"
              tableClassName="table-fixed"
              columns={columns}
              isLoading={library.loading}
              isEmpty={rows.length === 0 && subfolders.length === 0}
              error={library.error}
              onRetry={() => void library.refresh()}
              empty={
                filtering
                  ? {
                      title: "Nothing Matches",
                      message: "Try a different search, or remove a filter."
                    }
                  : openFolder
                  ? {
                      title: "This Folder Is Empty",
                      message: canEdit
                        ? "Drag documents onto it, or add documents while it is open."
                        : "Nothing has been filed here yet.",
                      cta: canEdit ? "Add Documents" : undefined,
                      onCta: canEdit ? () => setUploadOpen(true) : undefined
                    }
                  : view === UNFILED
                  ? {
                      title: "Everything Is Filed",
                      message: "Every document in the library is in a folder."
                    }
                  : {
                      title: "Show Your Agents Your Best Work",
                      message:
                        "Upload the briefs, letters and shells your firm is proud of. The agents read them before drafting and follow their shape and voice.",
                      cta: canEdit ? "Add Documents" : undefined,
                      onCta: canEdit ? () => setUploadOpen(true) : undefined
                    }
              }
            >
              {subfolders.map((folder) => {
                const sub = childFolders(tree, folder.id).length;
                const count = counts.get(folder.id) ?? 0;
                const under = new Set(descendantIds(tree, folder.id));
                const latest = visible
                  .filter((e) => e.folder_id && under.has(e.folder_id))
                  .map((e) => e.updated_at ?? e.created_at)
                  .filter(Boolean)
                  .sort()
                  .pop();
                return (
                  <ClientTableRow
                    key={`folder-${folder.id}`}
                    data-testid="library-folder-row"
                    ariaLabel={folder.name}
                    onActivate={() => openScope(folder.id)}
                    className={cn(
                      overFolderRow === folder.id &&
                        "bg-accent-soft ring-1 ring-accent-line ring-inset"
                    )}
                    {...folderRowDrop(folder.id)}
                  >
                    {canEdit ? <ClientTableCell /> : null}
                    <TitleCell
                      leading={
                        <Folder className="size-5 text-fg-3" aria-hidden />
                      }
                      title={folder.name}
                      subtitle={[
                        plural(count, "document"),
                        sub ? plural(sub, "folder") : null
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    />
                    <MutedCell>Folder</MutedCell>
                    <MutedCell>—</MutedCell>
                    <ClientTableCell />
                    <MutedCell>{latest ? formatMdY(latest) : "—"}</MutedCell>
                  </ClientTableRow>
                );
              })}
              {rows.map((entry) => {
                const subtitle = documentSubtitle(entry);
                const checked = selected.includes(entry.id);
                const name =
                  entry.name || entry.original_filename || "Untitled";
                const filed = showPath && !isUnfiled(tree, entry);
                return (
                  <ClientTableRow
                    key={entry.id}
                    domId={`${ROW_ID}${entry.id}`}
                    data-testid="library-row"
                    ariaLabel={name}
                    onActivate={() => openEntry(entry)}
                    draggable={canEdit}
                    onDragStart={(e) => startEntryDrag(e, entry.id)}
                    onDragEnd={() => setDrag(null)}
                    className={cn(
                      "group/row",
                      (focusedId === entry.id || checked) && "bg-fill-2",
                      drag?.kind === "entries" &&
                        drag.ids.includes(entry.id) &&
                        "opacity-40"
                    )}
                  >
                    {canEdit ? (
                      <ClientTableCell className="justify-center px-0">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(value) =>
                            setSelected((current) =>
                              value === true
                                ? [...current, entry.id]
                                : current.filter((id) => id !== entry.id)
                            )
                          }
                          aria-label={`Select ${name}`}
                        />
                      </ClientTableCell>
                    ) : null}
                    <TitleCell
                      leading={
                        <span className="relative flex size-8 items-center justify-center">
                          <span
                            className={cn(
                              "flex",
                              canEdit && "group-hover/row:invisible"
                            )}
                          >
                            <LibraryFileIcon entry={entry} />
                          </span>
                          {canEdit ? (
                            <GripVertical
                              className="invisible absolute size-4 cursor-grab text-fg-3 group-hover/row:visible"
                              aria-hidden
                            />
                          ) : null}
                        </span>
                      }
                      title={name}
                      subtitle={
                        filed ? (
                          <span
                            className="flex min-w-0 items-center gap-1"
                            data-testid="library-row-path"
                          >
                            <Folder className="size-3 shrink-0" aria-hidden />
                            <span className="truncate">
                              {folderPathLabel(tree, entry.folder_id)}
                            </span>
                          </span>
                        ) : entry.tags.length ? (
                          <HashTags tags={entry.tags} />
                        ) : subtitle ? (
                          <span
                            className={entry.description ? undefined : "italic"}
                          >
                            {subtitle}
                          </span>
                        ) : null
                      }
                    />
                    <MutedCell>{CATEGORY_LABEL[categoryOf(entry)]}</MutedCell>
                    <MutedCell>
                      {entry.page_count == null
                        ? "—"
                        : String(entry.page_count)}
                    </MutedCell>
                    <ClientTableCell>
                      <div className="flex flex-wrap items-center gap-1">
                        <ReadinessBadge entry={entry} />
                        <ConversionBadge entry={entry} compact />
                        <NotReviewedBadge entry={entry} />
                        <ClientDetailsBadge entry={entry} />
                        {canEdit && readinessOf(entry) === "attention" ? (
                          <AttentionActions
                            entry={entry}
                            scope={scope}
                            onRetried={() => void library.refresh()}
                            onRemoved={library.removeEntry}
                          />
                        ) : null}
                      </div>
                    </ClientTableCell>
                    <MutedCell>
                      {formatMdY(entry.updated_at ?? entry.created_at)}
                    </MutedCell>
                  </ClientTableRow>
                );
              })}
            </ClientTable>

            {canEdit && selected.length > 0 ? (
              <div
                className="absolute bottom-4 left-1/2 z-20 flex -translate-x-1/2 items-center gap-1 rounded-lg bg-elevated py-1 pr-1 pl-3 whitespace-nowrap shadow-popover ring-1 ring-line-3"
                data-testid="bulk-actions"
              >
                <span className="mr-1.5 text-base font-medium text-fg">
                  {selected.length} Selected
                </span>
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() =>
                    setMoveSubject({
                      kind: "entries",
                      ids: selected,
                      name:
                        selected.length === 1
                          ? library.entries.find((e) => e.id === selected[0])
                              ?.name
                          : undefined
                    })
                  }
                  data-testid="bulk-move"
                >
                  <FolderInput />
                  Move To…
                </Button>
                <Button
                  variant="ghost"
                  size="icon-xs"
                  aria-label="Clear Selection"
                  onClick={() => setSelected([])}
                >
                  <X />
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      {peekEntry ? (
        <LibraryPeek entry={peekEntry} onOpen={() => openEntry(peekEntry)} />
      ) : null}

      <UploadDialog
        open={uploadOpen}
        onOpenChange={(open) => {
          setUploadOpen(open);
          if (!open) setDroppedFiles(null);
        }}
        scope={scope}
        entries={library.entries}
        folders={library.folders}
        defaultFolderId={openFolderId}
        initialCollected={droppedFiles}
        onBatchStarted={() => void library.refresh()}
        onOpenEntry={(entryId) => {
          setOpenId(entryId);
          setEntryParam(entryId);
        }}
      />
      <EntryDrawer
        entry={openEntryRow}
        open={openEntryRow !== null}
        onOpenChange={closeDrawer}
        canEdit={canEdit}
        showTechnical={firm.isSuperAdmin}
        scope={scope}
        onChanged={library.replaceEntry}
        onDeleted={library.removeEntry}
        onQueued={() => void library.refresh()}
        onOpenEntry={(entryId) => {
          setOpenId(entryId);
          setEntryParam(entryId);
        }}
        location={
          openEntryRow
            ? {
                label: folderPathLabel(tree, openEntryRow.folder_id),
                agentPath: agentLibraryPath(tree, openEntryRow),
                onMove: canEdit
                  ? () =>
                      setMoveSubject({
                        kind: "entries",
                        ids: [openEntryRow.id],
                        name: openEntryRow.name
                      })
                  : undefined
              }
            : undefined
        }
      />
      <MoveToDialog
        subject={moveSubject}
        onOpenChange={(open) => !open && setMoveSubject(null)}
        tree={tree}
        current={moveCurrent}
        canCreate={canEdit}
        onMove={applyMove}
        onCreateFolder={createFolder}
      />
      <AlertDialog
        open={deleteOpen && deletePrompt !== null}
        onOpenChange={setDeleteOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deletePrompt?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              {deletePrompt?.subfolders
                ? `It and its ${plural(
                    deletePrompt.subfolders,
                    "subfolder"
                  )} go; their `
                : "Its "}
              {plural(deletePrompt?.documents ?? 0, "document")} move to
              Unfiled. No document is deleted, and agents keep reading them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => deletePrompt && void deleteFolder(deletePrompt.id)}
              data-testid="confirm-delete-folder"
            >
              Delete Folder
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
